import { inject, Injectable, NgZone } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  LogLevel,
} from '@microsoft/signalr';
import { Observable, Subject } from 'rxjs';
import { IdleTimer } from '../../../shared/utils/idle-timer';
import { environment } from '../../../../environments/environment';
import {
  ContainerInvalidatedNotification,
  EquipmentInvalidatedNotification,
} from '../../models/telemetry-signalr.model';
import { OAuthService } from 'angular-oauth2-oidc';
import {
  SIGNALR_IDLE_TIMEOUT_MINUTES,
  SIGNALR_RECONNECT_INTERVALS_MS,
} from '../../../shared/const/signalr.constants';

export type SubscriptionType = 'container' | 'facility' | 'equipment';

interface Subscription {
  type: SubscriptionType;
  id: string;
}

export interface StopOptions {
  strict?: boolean;
}

export const TELEMETRY_SIGNALR_STOP_OPTIONS = {
  graceful: { strict: false },
  strict: { strict: true },
} as const;

@Injectable()
export class TelemetrySignalRService {
  private readonly oauthService = inject(OAuthService);
  private readonly zone = inject(NgZone);

  private connection?: HubConnection;
  private startPromise?: Promise<void>;
  private stopPromise?: Promise<void>;
  private stopError?: unknown;
  private isDisposing = false;
  private lifecycleToken = 0;
  private readonly STOP_TIMEOUT_MS = 3000;

  private readonly invalidatedSubject = new Subject<ContainerInvalidatedNotification>();
  private readonly equipmentInvalidatedSubject = new Subject<EquipmentInvalidatedNotification>();
  private readonly idleTimeoutSubject = new Subject<void>();
  private readonly IDLE_TIMEOUT_MS =
    (environment.telemetry?.staleDataTimeoutMinutes ?? SIGNALR_IDLE_TIMEOUT_MINUTES) * 60 * 1000;
  private readonly idleTimer = new IdleTimer(this.IDLE_TIMEOUT_MS, () => {
    this.zone.run(() => this.idleTimeoutSubject.next());
  });
  private readonly activeSubscriptions = new Map<string, Subscription>();

  public invalidated$(): Observable<ContainerInvalidatedNotification> {
    return this.invalidatedSubject.asObservable();
  }

  public equipmentInvalidated$(): Observable<EquipmentInvalidatedNotification> {
    return this.equipmentInvalidatedSubject.asObservable();
  }

  public idleTimeout$(): Observable<void> {
    return this.idleTimeoutSubject.asObservable();
  }

  public async start(): Promise<void> {
    if (this.connection?.state === HubConnectionState.Connected || this.isDisposing) return;
    if (this.startPromise) return this.startPromise;
    const token = this.lifecycleToken;

    this.connection = this.buildConnection();
    this.registerHandlers(this.connection);

    this.startPromise = this.connection
      .start()
      .then(async () => {
        if (this.isDisposing || token !== this.lifecycleToken) return;
        this.resetIdleTimer();
        const subscriptions = Array.from(this.activeSubscriptions.values());
        for (const sub of subscriptions) {
          await this.safeInvokeSubscribe(sub.type, sub.id);
        }
      })
      .finally(() => {
        this.startPromise = undefined;
      });

    return this.startPromise;
  }

  public async stop(options: StopOptions = TELEMETRY_SIGNALR_STOP_OPTIONS.graceful): Promise<void> {
    const stopOptions = { ...TELEMETRY_SIGNALR_STOP_OPTIONS.graceful, ...options };
    const { strict } = stopOptions;
    if (this.stopPromise) {
      return this.waitForStopToFinish(stopOptions);
    }

    const token = ++this.lifecycleToken;
    this.isDisposing = true;
    this.stopError = undefined;
    const connectionToStop = this.connection;

    this.stopPromise = (async (): Promise<void> => {
      this.idleTimer.stop();

      if (!connectionToStop) {
        this.activeSubscriptions.clear();
        this.startPromise = undefined;
        this.connection = undefined;
        return;
      }

      try {
        await this.stopWithTimeout(connectionToStop);
      } catch (error) {
        this.stopError = error;
        if (strict) {
          throw error;
        }
        console.error('Failed to stop telemetry connection', error);
      } finally {
        if (this.connection === connectionToStop) {
          this.connection = undefined;
        }
        this.startPromise = undefined;
        this.activeSubscriptions.clear();
      }
    })().finally(() => {
      if (this.lifecycleToken === token) {
        this.isDisposing = false;
      }
      this.stopPromise = undefined;
    });

    return this.stopPromise;
  }

  public stopGracefully(): Promise<void> {
    return this.stop(TELEMETRY_SIGNALR_STOP_OPTIONS.graceful);
  }

  public stopStrictly(): Promise<void> {
    return this.stop(TELEMETRY_SIGNALR_STOP_OPTIONS.strict);
  }

  private async waitForStopToFinish(stopOptions: StopOptions): Promise<void> {
    const { strict = false } = stopOptions;
    if (!this.stopPromise) return;

    try {
      await this.stopPromise;
    } catch (error) {
      if (strict) {
        throw error;
      }
      return;
    }

    if (strict && this.stopError !== undefined) {
      throw this.stopError;
    }
  }

  public async subscribeToContainer(containerId: string): Promise<void> {
    await this.waitForNotDisposing();

    const key = this.getSubscriptionKey('container', containerId);
    const wasEmpty = this.activeSubscriptions.size === 0;
    this.activeSubscriptions.set(key, { type: 'container', id: containerId });
    await this.start();
    await this.safeInvokeSubscribe('container', containerId);
    if (wasEmpty) this.resetIdleTimer();
  }

  public async unsubscribeFromContainer(containerId: string): Promise<void> {
    const key = this.getSubscriptionKey('container', containerId);
    this.activeSubscriptions.delete(key);
    if (this.isDisposing) return;
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) return;
    try {
      await this.connection.invoke('UnsubscribeFromContainer', containerId);
    } catch (error) {
      console.debug('Failed to unsubscribe from container', containerId, error);
    }
  }

  public async subscribeToFacility(facilityId: string): Promise<void> {
    await this.waitForNotDisposing();

    const key = this.getSubscriptionKey('facility', facilityId);
    const wasEmpty = this.activeSubscriptions.size === 0;
    this.activeSubscriptions.set(key, { type: 'facility', id: facilityId });
    await this.start();
    await this.safeInvokeSubscribe('facility', facilityId);
    if (wasEmpty) this.resetIdleTimer();
  }

  public async unsubscribeFromFacility(facilityId: string): Promise<void> {
    const key = this.getSubscriptionKey('facility', facilityId);
    this.activeSubscriptions.delete(key);
    if (this.isDisposing) return;
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) return;
    try {
      await this.connection.invoke('UnsubscribeFromFacility', facilityId);
    } catch (error) {
      console.debug('Failed to unsubscribe from facility', facilityId, error);
    }
  }

  public async subscribeToEquipment(equipmentId: string): Promise<void> {
    await this.waitForNotDisposing();

    const key = this.getSubscriptionKey('equipment', equipmentId);
    const wasEmpty = this.activeSubscriptions.size === 0;
    this.activeSubscriptions.set(key, { type: 'equipment', id: equipmentId });
    await this.start();
    await this.safeInvokeSubscribe('equipment', equipmentId);
    if (wasEmpty) this.resetIdleTimer();
  }

  public async unsubscribeFromEquipment(equipmentId: string): Promise<void> {
    const key = this.getSubscriptionKey('equipment', equipmentId);
    this.activeSubscriptions.delete(key);
    if (this.isDisposing) return;
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) return;
    try {
      await this.connection.invoke('UnsubscribeFromEquipment', equipmentId);
    } catch (error) {
      console.debug('Failed to unsubscribe from equipment', equipmentId, error);
    }
  }

  public async unsubscribeAll(): Promise<void> {
    const subscriptions = Array.from(this.activeSubscriptions.values());
    for (const sub of subscriptions) {
      if (sub.type === 'container') {
        await this.unsubscribeFromContainer(sub.id);
      } else if (sub.type === 'facility') {
        await this.unsubscribeFromFacility(sub.id);
      } else {
        await this.unsubscribeFromEquipment(sub.id);
      }
    }
  }

  public getActiveContainerIds(): string[] {
    return Array.from(this.activeSubscriptions.values())
      .filter((s) => s.type === 'container')
      .map((s) => s.id);
  }

  public getActiveFacilityIds(): string[] {
    return Array.from(this.activeSubscriptions.values())
      .filter((s) => s.type === 'facility')
      .map((s) => s.id);
  }

  public getActiveEquipmentIds(): string[] {
    return Array.from(this.activeSubscriptions.values())
      .filter((s) => s.type === 'equipment')
      .map((s) => s.id);
  }

  private resetIdleTimer(): void {
    if (this.activeSubscriptions.size === 0) {
      this.idleTimer.stop();
      return;
    }

    this.idleTimer.reset();
  }

  private buildConnection(): HubConnection {
    const hubUrl = `${environment.apiUrls.bffAdmin}/hub/telemetry`;
    return new HubConnectionBuilder()
      .withUrl(hubUrl, {
        withCredentials: false,
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
        accessTokenFactory: () => this.oauthService.getAccessToken() ?? '',
      })
      .withAutomaticReconnect([...SIGNALR_RECONNECT_INTERVALS_MS])
      .configureLogging(environment.production ? LogLevel.Critical : LogLevel.Information)
      .build();
  }

  private registerHandlers(conn: HubConnection): void {
    conn.on('container.invalidated', (payload: ContainerInvalidatedNotification) => {
      this.resetIdleTimer();
      this.zone.run(() => this.invalidatedSubject.next(payload));
    });

    conn.on('equipment.invalidated', (payload: EquipmentInvalidatedNotification) => {
      this.resetIdleTimer();
      this.zone.run(() => this.equipmentInvalidatedSubject.next(payload));
    });

    conn.onreconnected(() => {
      if (this.isDisposing) return;

      this.resetIdleTimer();
      void (async (): Promise<void> => {
        const subscriptions = Array.from(this.activeSubscriptions.values());
        for (const sub of subscriptions) {
          await this.safeInvokeSubscribe(sub.type, sub.id);
        }
      })();
    });
  }

  private async safeInvokeSubscribe(type: SubscriptionType, id: string): Promise<void> {
    if (!this.isSubscriptionActive(type, id)) return;
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) return;
    try {
      await this.connection.invoke(this.getSubscribeMethod(type), id);

      if (!this.isSubscriptionActive(type, id)) {
        await this.safeInvokeUnsubscribe(type, id, 'subscription_removed_during_subscribe');
      }
    } catch (error) {
      console.debug('Failed to subscribe, will retry on reconnection', { type, id }, error);
    }
  }

  private async stopWithTimeout(connection: HubConnection): Promise<void> {
    return this.promiseWithTimeout(connection.stop(), this.STOP_TIMEOUT_MS);
  }

  private async waitForNotDisposing(): Promise<void> {
    while (this.isDisposing) {
      const currentStopPromise = this.stopPromise;
      if (!currentStopPromise) return;
      await currentStopPromise;
    }
  }

  private promiseWithTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<T>((_resolve, reject) => {
      timeoutId = setTimeout(() => {
        reject(new Error(`Operation timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    });

    return Promise.race([promise, timeoutPromise]).finally(() => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    });
  }

  private getSubscriptionKey(type: SubscriptionType, id: string): string {
    return `${type}-${id}`;
  }

  private isSubscriptionActive(type: SubscriptionType, id: string): boolean {
    return this.activeSubscriptions.has(this.getSubscriptionKey(type, id));
  }

  private getSubscribeMethod(type: SubscriptionType): string {
    return type === 'container'
      ? 'SubscribeToContainer'
      : type === 'facility'
        ? 'SubscribeToFacility'
        : 'SubscribeToEquipment';
  }

  private getUnsubscribeMethod(type: SubscriptionType): string {
    return type === 'container'
      ? 'UnsubscribeFromContainer'
      : type === 'facility'
        ? 'UnsubscribeFromFacility'
        : 'UnsubscribeFromEquipment';
  }

  private async safeInvokeUnsubscribe(
    type: SubscriptionType,
    id: string,
    reason: string,
  ): Promise<void> {
    if (!this.connection || this.connection.state !== HubConnectionState.Connected) return;
    try {
      await this.connection.invoke(this.getUnsubscribeMethod(type), id);
    } catch (error) {
      console.debug(
        'Failed to compensate stale telemetry subscription',
        { type, id, reason },
        error,
      );
    }
  }
}
